import { ReactNode, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { When } from "react-if";
import {
  CallOutcome,
  CallOutcomeLabels,
  ITeamActivityCounts,
  ITeamActivityUser,
  ITeamActivityWeek,
  ITeamFollowUp,
  ITeamFollowUps,
  TeamActivityPeriod,
  TeamActivityPeriodLabels,
  TeamFollowUpReason,
  TeamFollowUpReasonLabels,
} from "shared";

import { Badge } from "../../../common/Atoms/Badge";
import { Button } from "../../../common/Atoms/Button";
import { Modal } from "../../../common/Atoms/Modal";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Select } from "../../../common/Atoms/Select";
import { classNames } from "../../../common/lib/classNames";
import { formatAmount } from "../../../common/lib/helpers";
import { IDelta } from "../EmailPerformance/stats";
import { WrappedLink } from "../WrappedLink";

import {
  niceTop,
  pct,
  relativeTime,
  weekActivities,
  weekLabel,
} from "./stats";

export const TYPE_COLORS = {
  calls: `#2b495a`,
  texts: `#6196b3`,
  emails: `#80cbc4`,
};

const USER_COLORS = [`#386075`, `#467691`, `#6196b3`, `#2b495a`, `#0D8ABC`, `#80cbc4`, `#8ab1c7`, `#ff6566`];

export function userColor(index: number) {
  return USER_COLORS[index % USER_COLORS.length];
}

/** Display order and colour for each call outcome */
const OUTCOMES: Array<{ outcome: CallOutcome; color: string }> = [
  { outcome: CallOutcome.SPOKE_TO_CUSTOMER, color: `#2b495a` },
  { outcome: CallOutcome.NO_ANSWER, color: `#d1d5db` },
  { outcome: CallOutcome.LEFT_VOICEMAIL, color: `#8ab1c7` },
  { outcome: CallOutcome.CALLBACK_REQUESTED, color: `#467691` },
  { outcome: CallOutcome.NOT_AVAILABLE, color: `#9ca3af` },
  { outcome: CallOutcome.WRONG_NUMBER, color: `#ff6566` },
  { outcome: CallOutcome.CALL_FAILED, color: `#c11415` },
  { outcome: CallOutcome.OTHER, color: `#e5e7eb` },
];

export function outcomeColor(outcome: CallOutcome | null) {
  return OUTCOMES.find(o => o.outcome === outcome)?.color ?? TYPE_COLORS.calls;
}

const FOLLOW_UP_BADGES: Record<TeamFollowUpReason, `indigo` | `red` | `amber` | `gray`> = {
  [TeamFollowUpReason.CALLBACK_REQUESTED]: `indigo`,
  [TeamFollowUpReason.BROKEN_PROMISE]: `red`,
  [TeamFollowUpReason.ESCALATION_PAST_SCHEDULED]: `amber`,
  [TeamFollowUpReason.WRONG_NUMBER]: `gray`,
};

const FOLLOW_UP_NOUNS: Record<TeamFollowUpReason, [string, string]> = {
  [TeamFollowUpReason.CALLBACK_REQUESTED]: [`callback requested`, `callbacks requested`],
  [TeamFollowUpReason.BROKEN_PROMISE]: [`broken promise`, `broken promises`],
  [TeamFollowUpReason.ESCALATION_PAST_SCHEDULED]: [`escalation past its scheduled date`, `escalations past their scheduled date`],
  [TeamFollowUpReason.WRONG_NUMBER]: [`wrong number`, `wrong numbers`],
};

export const linkClass = `font-medium text-paidniceGreen-400 underline`;

export const cardClass = `rounded-lg bg-white p-5 shadow-xs`;
const thClass = `px-3 py-2.5 font-semibold`;

/** The period lives in the URL so it carries between the team page and the user pages */
export function usePeriod() {
  const [params, setParams] = useSearchParams();
  const raw = params.get(`period`) as TeamActivityPeriod;
  const period = Object.values(TeamActivityPeriod).includes(raw) ? raw : TeamActivityPeriod.LAST_30_DAYS;
  const setPeriod = (value: TeamActivityPeriod) => setParams(prev => {
    const next = new URLSearchParams(prev);
    next.set(`period`, value);

    return next;
  }, { replace: true });

  return { period, setPeriod };
}

const periodOptions = Object.values(TeamActivityPeriod).map(value => ({ label: TeamActivityPeriodLabels[value], value }));

export function PeriodSelect({ period, onChange }: { period: TeamActivityPeriod; onChange: (p: TeamActivityPeriod) => void }) {
  return (
    <div className={ `w-40` }>
      <Select
        options={ periodOptions }
        selected={ period }
        onChange={ ({ value }) => onChange(value as TeamActivityPeriod) }
        size={ `sm` }
      />
    </div>
  );
}

export function userPath(userId: string, period: TeamActivityPeriod) {
  return `/team-activity/users/${userId}?period=${period}`;
}

export function UserLink({ user, className }: { user: ITeamActivityUser; className?: string }) {
  const { period } = usePeriod();

  return (
    <WrappedLink to={ userPath(user.id, period) }
      className={ classNames(`font-medium underline`, className) }>
      { user.name }
    </WrappedLink>
  );
}

export function ContactLink({ id, name }: { id: number; name: string }) {
  return (
    <WrappedLink to={ `/contacts/${id}` }
      className={ linkClass }>
      { name }
    </WrappedLink>
  );
}

export function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join(``);
}

type Tone = `good` | `bad` | `flat`;

/** Whether a move is good depends on the metric: goodWhen null means neutral */
export function deltaTone(delta: IDelta | null, goodWhen: `up` | `down` | null): Tone {
  if (!delta || delta.direction === `flat` || !goodWhen) {
    return `flat`;
  }

  return delta.direction === goodWhen ? `good` : `bad`;
}

export const toneOnLight: Record<Tone, string> = {
  good: `text-emerald-700`,
  bad: `text-paidniceRed-700`,
  flat: `text-gray-500`,
};

export const toneOnDark: Record<Tone, string> = {
  good: `text-emerald-200 dark:text-[#a7f3d0]`,
  bad: `text-paidniceRed-200`,
  flat: `text-paidniceGreen-300`,
};

export function ErrorState({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className={ `flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white text-center` }>
      <Paragraph variant={ `secondary` }>
        { `We couldn't load ${what}. Please try again.` }
      </Paragraph>
      <Button color={ `paidniceGreen` }
        onClick={ onRetry }>
        { `Retry` }
      </Button>
    </div>
  );
}

export function CardHeading({ title, subtitle, action }: { title: string; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className={ `flex flex-wrap items-start justify-between gap-x-6 gap-y-2` }>
      <div className={ `min-w-0 flex-1` }>
        <h3 className={ `text-base font-semibold text-gray-900` }>{ title }</h3>
        <When condition={ !!subtitle }>
          <div className={ `mt-1 text-sm text-gray-500` }>{ subtitle }</div>
        </When>
      </div>
      { action }
    </div>
  );
}

export function SegmentedToggle<T extends string>({ options, value, onChange }: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className={ `flex shrink-0 flex-wrap gap-0.5 rounded-lg bg-gray-100 p-0.5` }>
      { options.map(o => (
        <button key={ o.value }
          type={ `button` }
          onClick={ () => onChange(o.value) }
          aria-pressed={ o.value === value }
          className={ classNames(
            `cursor-pointer rounded-md px-3 py-1 text-xs font-semibold`,
            o.value === value ? `bg-paidniceGreen-700 text-white` : `text-gray-600 hover:text-gray-900`,
          ) }>
          { o.label }
        </button>
      )) }
    </div>
  );
}

export function LegendItem({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className={ `flex items-center gap-1.5 text-xs text-gray-500` }>
      { dashed
        ? <span className={ `w-3.5 border-t-2 border-dashed border-gray-400` } />
        : <span className={ `h-2.5 w-2.5 rounded-xs` }
          style={ { background: color } } /> }
      { label }
    </span>
  );
}

export function TypeLegend() {
  return (
    <>
      <LegendItem color={ TYPE_COLORS.calls }
        label={ `Phone calls` } />
      <LegendItem color={ TYPE_COLORS.texts }
        label={ `Text messages` } />
      <LegendItem color={ TYPE_COLORS.emails }
        label={ `External emails` } />
    </>
  );
}

interface IWeeklyChartProps {
  weeks: ITeamActivityWeek[];
  /** By user stacks each user's activities. Needs the users to colour and name the segments */
  mode: `type` | `user`;
  users?: ITeamActivityUser[];
  /** Draws a dashed line at this many activities per week */
  average?: number;
}

const CHART_HEIGHT = `h-[170px]`;

export function WeeklyChart({ weeks, mode, users = [], average }: IWeeklyChartProps) {
  const stacks = weeks.map(w => {
    const segments = mode === `type`
      ? [
        { label: `Phone calls`, value: w.phoneCalls, color: TYPE_COLORS.calls },
        { label: `Text messages`, value: w.textMessages, color: TYPE_COLORS.texts },
        { label: `External emails`, value: w.externalEmails, color: TYPE_COLORS.emails },
      ]
      : users.map((u, i) => ({ label: u.name, value: w.byUser[u.id] ?? 0, color: userColor(i) }));
    const total = mode === `type` ? weekActivities(w) : segments.reduce((sum, s) => sum + s.value, 0);

    return { week: w, segments: segments.filter(s => s.value > 0), total };
  });
  const top = niceTop(Math.max(...stacks.map(s => s.total), average ?? 0));
  const ticks = [0, 1, 2, 3, 4].map(i => (top / 4) * i);

  return (
    <div className={ `flex gap-2` }>
      <div className={ `-mt-1.5 flex ${CHART_HEIGHT} w-8 shrink-0 flex-col-reverse justify-between text-right text-[10.5px] text-gray-400` }>
        { ticks.map(t => <span key={ t }>{ t }</span>) }
      </div>
      <div className={ `flex min-w-0 flex-1 flex-col gap-1.5` }>
        <div className={ `relative flex ${CHART_HEIGHT} items-end border-b border-gray-200` }
          style={ { backgroundImage: `linear-gradient(var(--color-gray-200) 1px, transparent 1px)`, backgroundSize: `100% 25%` } }>
          { stacks.map(({ week, segments, total }) => (
            <div key={ week.weekStart }
              className={ `flex h-full flex-1 items-end justify-center` }>
              <div className={ `flex w-full max-w-9 flex-col-reverse overflow-hidden rounded-t-[3px]` }
                style={ { height: `${(total / top) * 100}%` } }
                title={ `Week of ${weekLabel(week.weekStart)}: ${segments.map(s => `${s.label} ${s.value}`).join(`, `) || `no activity`}` }>
                { segments.map(s => (
                  <div key={ s.label }
                    style={ { height: `${(s.value / total) * 100}%`, background: s.color } } />
                )) }
              </div>
            </div>
          )) }
          <When condition={ average !== undefined }>
            <div className={ `pointer-events-none absolute inset-x-0 border-t-2 border-dashed border-gray-400` }
              style={ { bottom: `${((average ?? 0) / top) * 100}%` } } />
          </When>
        </div>
        <div className={ `flex text-[10.5px] text-gray-400` }>
          { weeks.map(w => (
            <span key={ w.weekStart }
              className={ `flex-1 truncate text-center` }>
              { weekLabel(w.weekStart) }
            </span>
          )) }
        </div>
      </div>
    </div>
  );
}

/** Call outcome bars, scaled to the most common outcome. Pass the team's counts to show its share alongside */
export function OutcomeBars({ counts, teamCounts }: { counts: ITeamActivityCounts; teamCounts?: ITeamActivityCounts }) {
  const biggest = Math.max(...OUTCOMES.map(o => counts.callOutcomes[o.outcome] ?? 0), 1);
  const share = (n: number, of: number) => pct(of > 0 ? (n / of) * 100 : null, n / (of || 1) < 0.05 ? 1 : 0);

  return (
    <>
      { OUTCOMES.map(({ outcome, color }) => {
        const n = counts.callOutcomes[outcome] ?? 0;

        return (
          <div key={ outcome }
            className={ `flex flex-col gap-1` }>
            <div className={ `flex justify-between gap-2 text-sm` }>
              <span className={ `text-gray-700` }>{ CallOutcomeLabels[outcome] }</span>
              <span className={ `font-medium tabular-nums text-gray-900` }>
                { `${n} · ${share(n, counts.phoneCalls)}` }
                <When condition={ !!teamCounts }>
                  <span className={ `font-normal text-gray-400` }>{ ` / ${share(teamCounts?.callOutcomes[outcome] ?? 0, teamCounts?.phoneCalls ?? 0)}` }</span>
                </When>
              </span>
            </div>
            <div className={ `h-1.5 rounded-full bg-gray-100` }>
              <div className={ `h-1.5 rounded-full` }
                style={ { width: `${(n / biggest) * 100}%`, background: color } } />
            </div>
          </div>
        );
      }) }
    </>
  );
}

function FollowUpTable({ items, showUser, homeCurrency }: { items: ITeamFollowUp[]; showUser: boolean; homeCurrency: string }) {
  return (
    <div className={ `overflow-x-auto` }>
      <table className={ `w-full border-collapse text-sm` }>
        <thead>
          <tr className={ `border-b border-gray-200 bg-gray-50 text-left` }>
            <th className={ thClass }>{ `Contact` }</th>
            <th className={ thClass }>{ `Reason` }</th>
            <th className={ thClass }>{ `Detail` }</th>
            { showUser && <th className={ thClass }>{ `User` }</th> }
            <th className={ thClass }>{ `Last activity` }</th>
            <th className={ `${thClass} text-right` }>{ `Contact balance` }</th>
          </tr>
        </thead>
        <tbody>
          { items.map(item => (
            <tr key={ `${item.reason}-${item.contactId}-${item.detail}` }
              className={ `border-b border-gray-200` }>
              <td className={ `px-3 py-2.5` }><ContactLink id={ item.contactId }
                name={ item.contactName } /></td>
              <td className={ `px-3 py-2.5` }>
                <Badge color={ FOLLOW_UP_BADGES[item.reason] }
                  message={ TeamFollowUpReasonLabels[item.reason] }
                  size={ `sm` } />
              </td>
              <td className={ `px-3 py-2.5 text-gray-700` }>{ item.detail }</td>
              { showUser && <td className={ `px-3 py-2.5 text-gray-700` }>{ item.userName ?? `-` }</td> }
              <td className={ `whitespace-nowrap px-3 py-2.5 text-gray-700` }>{ relativeTime(item.lastActivityAt) }</td>
              <td className={ `whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-900` }>{ formatAmount(item.contactBalanceHomeCents, homeCurrency) }</td>
            </tr>
          )) }
        </tbody>
      </table>
    </div>
  );
}

const PREVIEW_ROWS = 5;

interface IFollowUpsCardProps {
  followUps: ITeamFollowUps;
  homeCurrency: string;
  /** Shows who each follow up belongs to. Left off on a user's own page */
  showUser: boolean;
  /** Name for the sentence under the title on a user's page */
  forUser?: string;
}

/** Top few follow ups, with a modal listing every one the API returned */
export function FollowUpsCard({ followUps, homeCurrency, showUser, forUser }: IFollowUpsCardProps) {
  const [open, setOpen] = useState(false);
  const { items, total } = followUps;
  const summary = Object.values(TeamFollowUpReason)
    .filter(reason => followUps.counts[reason] > 0)
    .map(reason => `${followUps.counts[reason]} ${FOLLOW_UP_NOUNS[reason][followUps.counts[reason] === 1 ? 0 : 1]}`)
    .join(`, `);

  return (
    <div className={ cardClass }>
      <CardHeading
        title={ `Needs follow up` }
        subtitle={ total === 0 ? `Nothing needs follow up.` : `${summary}${forUser ? ` for ${forUser}` : ``}. Largest balances first.` }
        action={ items.length > PREVIEW_ROWS && (
          <Button color={ `paidniceGreenOutline` }
            onClick={ () => setOpen(true) }>
            { `View all ${total}` }
          </Button>
        ) } />
      <When condition={ items.length > 0 }>
        <div className={ `mt-3` }>
          <FollowUpTable items={ items.slice(0, PREVIEW_ROWS) }
            showUser={ showUser }
            homeCurrency={ homeCurrency } />
        </div>
      </When>
      <Modal open={ open }
        setOpen={ setOpen }
        width={ `xl` }
        title={ `Needs follow up` }>
        <div className={ `mt-3 max-h-[70vh] overflow-y-auto` }>
          <FollowUpTable items={ items }
            showUser={ showUser }
            homeCurrency={ homeCurrency } />
        </div>
        <When condition={ total > items.length }>
          <div className={ `mt-3 text-sm text-gray-500` }>{ `Showing the ${items.length} largest balances of ${total}.` }</div>
        </When>
      </Modal>
    </div>
  );
}
